import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export type DashboardRole = 'super_admin' | 'trainer';

export interface User {
  id: number;
  email: string;
  role: DashboardRole;
  is_super_admin: boolean;
  is_trainer: boolean;
}

interface BasicAuthUser {
  id: number;
  email: string;
}

export interface AdminUser {
  id: number;
  email: string;
  created_at: string;
  last_login: string | null;
  target_carbs?: number | null;
  target_protein?: number | null;
  target_fat?: number | null;
  is_trainer?: boolean;
  is_super_admin?: boolean;
  trainer_user_id?: number | null;
  trainer_email?: string | null;
}

export interface TrainerUser {
  id: number;
  email: string;
  created_at: string;
  last_login: string | null;
  client_count: number;
  is_super_admin: boolean;
}

export interface GroupUserCandidate extends AdminUser {
  last_meal_logged: string | null;
  total_meals: number;
  active_days: number;
}

export interface AdminGroupMemberMacroSignal {
  actual: number | null;
  target: number | null;
  pct_of_target: number | null;
  delta_pct: number | null;
  direction: 'under' | 'on' | 'over' | 'missing_target' | 'no_log';
}

export interface AdminGroupMemberRecentDay {
  date: string;
  logged: boolean;
  meal_count: number;
  on_target: boolean | null;
  macros: {
    carbs: AdminGroupMemberMacroSignal;
    protein: AdminGroupMemberMacroSignal;
    fat: AdminGroupMemberMacroSignal;
  };
}

export interface AdminGroupMember extends GroupUserCandidate {
  days_logged_last_5: number;
  days_logged_last_7: number;
  days_logged_last_30: number;
  meals_last_7: number;
  meals_last_30: number;
  days_on_target_last_5: number | null;
  days_on_target_last_7: number | null;
  days_on_target_last_30: number | null;
  recent_hit_rate_pct: number | null;
  recent_days: AdminGroupMemberRecentDay[];
}

export interface AdminGroupSummary {
  id: number;
  name: string;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminGroup extends AdminGroupSummary {
  memberUserIds: number[];
  members: AdminGroupMember[];
}

export interface DailySummary {
  date: string;
  total_carbs: number;
  total_protein: number;
  total_fat: number;
  total_calories: number;
  item_count: number;
}

export interface MealItem {
  id: number;
  food_name: string;
  qty?: number;
  unit?: string;
  notes?: string;
  carbs: number;
  protein: number;
  fat: number;
  calories: number;
  meal_time: string;
  conversation_id?: string;
  dish_session_id?: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MessageHistoryEntry {
  id: number;
  text: string;
  imageUrl?: string | null;
  action: 'created' | 'updated';
  timestamp: string;
}

export interface MealItemWithHistory extends MealItem {
  messageHistory: MessageHistoryEntry[];
}

export interface OrphanMessage {
  id: number;
  conversation_id: string;
  text: string;
  imageUrl?: string | null;
  itemIds: number[];
  action: string;
  timestamp: string;
}

export interface AdminDayConversationsResponse {
  success: boolean;
  items: MealItemWithHistory[];
  orphanMessages: OrphanMessage[];
  date: string;
  error?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: BasicAuthUser;
  message?: string;
  error?: string;
}

export interface AdminMeResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AdminUsersResponse {
  success: boolean;
  viewerRole: DashboardRole;
  users: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

export interface AdminUserDetailsResponse {
  success: boolean;
  user: AdminUser;
  dailySummaries: DailySummary[];
  error?: string;
}

export interface AdminUserDayDetailsResponse {
  success: boolean;
  items: MealItem[];
  date: string;
  error?: string;
}

export interface TrainerListResponse {
  success: boolean;
  trainers: TrainerUser[];
  error?: string;
}

export interface AdminGroupListResponse {
  success: boolean;
  groups: AdminGroupSummary[];
  error?: string;
}

export interface AdminGroupUsersResponse {
  success: boolean;
  users: GroupUserCandidate[];
  error?: string;
}

export interface AdminGroupDetailsResponse {
  success: boolean;
  group: AdminGroup;
  error?: string;
}

export interface AdminGroupMutationResponse {
  success: boolean;
  group?: AdminGroupSummary;
  error?: string;
}

// Auth API
export const authAPI = {
  async sendCode(email: string): Promise<AuthResponse> {
    const response = await api.post('/auth/code', { email });
    return response.data;
  },

  async verifyCode(email: string, code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/verify', { email, code });
    return response.data;
  },

  async getAdminMe(token: string): Promise<AdminMeResponse> {
    const response = await api.get('/admin/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  async getMe(): Promise<AdminMeResponse> {
    const response = await api.get('/admin/me');
    return response.data;
  },

  async getUsers(page: number = 1, limit: number = 100): Promise<AdminUsersResponse> {
    const response = await api.get(`/admin/users?page=${page}&limit=${limit}`);
    return response.data;
  },

  async getTrainers(): Promise<TrainerListResponse> {
    const response = await api.get('/admin/trainers');
    return response.data;
  },

  async getGroups(): Promise<AdminGroupListResponse> {
    const response = await api.get('/admin/groups');
    return response.data;
  },

  async getGroupUsers(): Promise<AdminGroupUsersResponse> {
    const response = await api.get('/admin/groups/users');
    return response.data;
  },

  async getGroup(groupId: number): Promise<AdminGroupDetailsResponse> {
    const response = await api.get(`/admin/groups/${groupId}`);
    return response.data;
  },

  async createGroup(payload: { name: string; memberUserIds: number[] }): Promise<AdminGroupMutationResponse> {
    const response = await api.post('/admin/groups', payload);
    return response.data;
  },

  async updateGroup(groupId: number, payload: { name: string; memberUserIds: number[] }): Promise<AdminGroupMutationResponse> {
    const response = await api.put(`/admin/groups/${groupId}`, payload);
    return response.data;
  },

  async getUserDetails(userId: number): Promise<AdminUserDetailsResponse> {
    const timezoneOffset = new Date().getTimezoneOffset();
    const offsetString = timezoneOffset > 0
      ? `-${Math.floor(timezoneOffset / 60).toString().padStart(2, '0')}:${(timezoneOffset % 60).toString().padStart(2, '0')}`
      : `+${Math.floor(Math.abs(timezoneOffset) / 60).toString().padStart(2, '0')}:${(Math.abs(timezoneOffset) % 60).toString().padStart(2, '0')}`;

    const response = await api.get(`/admin/users/${userId}?timezone=${encodeURIComponent(offsetString)}`);
    return response.data;
  },

  async getUserDayDetails(userId: number, date: string): Promise<AdminUserDayDetailsResponse> {
    const response = await api.get(`/admin/users/${userId}/days/${date}`);
    return response.data;
  },

  async getUserDayConversations(userId: number, date: string): Promise<AdminDayConversationsResponse> {
    const response = await api.get(`/admin/users/${userId}/days/${date}/conversations`);
    return response.data;
  },

  async updateMeal(mealId: number, updates: Partial<MealItem>): Promise<{ success: boolean; item?: MealItem; error?: string }> {
    const response = await api.put(`/admin/meals/${mealId}`, updates);
    return response.data;
  },

  async deleteMeal(mealId: number): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await api.delete(`/admin/meals/${mealId}`);
    return response.data;
  },

  async createMeal(userId: number, mealData: {
    food_name: string;
    qty?: number;
    unit?: string;
    notes?: string;
    carbs?: number;
    protein?: number;
    fat?: number;
    calories?: number;
    meal_time: string;
  }): Promise<{ success: boolean; item?: MealItem; error?: string }> {
    const response = await api.post(`/admin/users/${userId}/meals`, mealData);
    return response.data;
  },

  async updateUserTargets(userId: number, targets: {
    carbs?: number | null;
    protein?: number | null;
    fat?: number | null;
  }): Promise<{ success: boolean; user?: AdminUser; error?: string }> {
    const response = await api.put(`/admin/users/${userId}/targets`, targets);
    return response.data;
  },

  async setUserTrainerStatus(userId: number, isTrainer: boolean): Promise<{ success: boolean; user?: AdminUser; error?: string }> {
    const response = await api.put(`/admin/users/${userId}/trainer-status`, { isTrainer });
    return response.data;
  },

  async assignTrainerToClient(userId: number, trainerUserId: number | null): Promise<{ success: boolean; user?: AdminUser; error?: string }> {
    const response = await api.put(`/admin/users/${userId}/trainer`, { trainerUserId });
    return response.data;
  },

  async getActiveUsers(days: number = 5, limit: number = 20): Promise<ActiveUsersResponse> {
    const timezoneOffset = new Date().getTimezoneOffset();
    const offsetString = timezoneOffset > 0
      ? `-${Math.floor(timezoneOffset / 60).toString().padStart(2, '0')}:${(timezoneOffset % 60).toString().padStart(2, '0')}`
      : `+${Math.floor(Math.abs(timezoneOffset) / 60).toString().padStart(2, '0')}:${(Math.abs(timezoneOffset) % 60).toString().padStart(2, '0')}`;
    const response = await api.get(`/admin/active-users?days=${days}&limit=${limit}&timezone=${encodeURIComponent(offsetString)}`);
    return response.data;
  },
};

export interface ActiveUserDailyData {
  date: string;
  totals: {
    carbs: number;
    protein: number;
    fat: number;
    calories: number;
    itemCount: number;
  } | null;
}

export interface ActiveUser {
  id: number;
  email: string;
  targets: {
    carbs: number | null;
    protein: number | null;
    fat: number | null;
  };
  totalMeals: number;
  dailyData: ActiveUserDailyData[];
}

export interface ActiveUsersResponse {
  success: boolean;
  users: ActiveUser[];
  dates: string[];
  dateRange: { start: string; days: number };
  error?: string;
}

export default api;
